import api from "./api";

const BASE = "dms";

const unwrap = (response) =>
  response && response.data !== undefined ? response.data : response;

export const buildQuery = (params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    if (typeof value === "boolean") {
      search.append(key, value ? "true" : "false");
      return;
    }
    search.append(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

const triggerDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "document";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const filenameFromDisposition = (disposition, fallback) => {
  if (!disposition) return fallback;
  const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
  if (!match || !match[1]) return fallback;
  return match[1].replace(/['"]/g, "");
};

export const getCategories = async (rootCode) => {
  const response = await api.getData(
    `${BASE}/categories${buildQuery(rootCode ? { rootCode } : {})}`
  );
  return unwrap(response);
};

export const getCategoryLeaves = async (rootCode) => {
  const response = await api.getData(
    `${BASE}/categories/leaves${buildQuery(rootCode ? { rootCode } : {})}`
  );
  return unwrap(response);
};

export const createCategory = async (body) => {
  const response = await api.postData(`${BASE}/categories`, body);
  return unwrap(response);
};

export const updateCategory = async (id, body) => {
  const response = await api.putData(`${BASE}/categories/${id}`, body);
  return unwrap(response);
};

export const deleteCategory = async (id) => {
  const response = await api.deleteData(`${BASE}/categories/${id}`);
  return unwrap(response);
};

export const getSystemDocuments = async (params) => {
  const response = await api.getData(
    `${BASE}/system-documents${buildQuery(params)}`
  );
  return unwrap(response);
};

export const uploadSystemDocument = async ({ categoryId, title, file }) => {
  const form = new FormData();
  form.append("categoryId", String(categoryId));
  form.append("title", title);
  form.append("file", file);
  const response = await api.postFormData(`${BASE}/system-documents`, form);
  return unwrap(response);
};

export const deleteSystemDocument = async (id) => {
  const response = await api.deleteData(`${BASE}/system-documents/${id}`);
  return unwrap(response);
};

export const downloadSystemDocument = async (id, fallbackName) => {
  const response = await api.getBlobData(`${BASE}/system-documents/${id}/download`);
  const blob = new Blob([response.data], {
    type: (response.headers && response.headers["content-type"]) || "application/octet-stream",
  });
  const name = filenameFromDisposition(
    response.headers && response.headers["content-disposition"],
    fallbackName || `system-document-${id}`
  );
  triggerDownload(blob, name);
};

export const getProjectsSummary = async () => {
  const response = await api.getData(`${BASE}/projects/summary`);
  return unwrap(response);
};

export const getProjectsMatrix = async (params) => {
  const response = await api.getData(`${BASE}/projects${buildQuery(params)}`);
  return unwrap(response);
};

export const getProjectDocuments = async (projectId) => {
  const response = await api.getData(`${BASE}/projects/${projectId}/documents`);
  return unwrap(response);
};

export const uploadProjectDocument = async (projectId, { categoryId, title, file }) => {
  const form = new FormData();
  form.append("categoryId", String(categoryId));
  form.append("title", title);
  form.append("file", file);
  const response = await api.postFormData(
    `${BASE}/projects/${projectId}/documents`,
    form
  );
  return unwrap(response);
};

export const approveDocument = async (id) => {
  const response = await api.postData(`${BASE}/documents/${id}/approve`, {});
  return unwrap(response);
};

export const rejectDocument = async (id, rejectionReason) => {
  const response = await api.postData(`${BASE}/documents/${id}/reject`, {
    rejectionReason: rejectionReason || "",
  });
  return unwrap(response);
};

export const deleteDocument = async (id) => {
  const response = await api.deleteData(`${BASE}/documents/${id}`);
  return unwrap(response);
};

export const downloadDocument = async (id, fallbackName) => {
  const response = await api.getBlobData(`${BASE}/documents/${id}/download`);
  const blob = new Blob([response.data], {
    type: (response.headers && response.headers["content-type"]) || "application/octet-stream",
  });
  const name = filenameFromDisposition(
    response.headers && response.headers["content-disposition"],
    fallbackName || `document-${id}`
  );
  triggerDownload(blob, name);
};

export const getMyDocuments = async () => {
  const response = await api.getData(`${BASE}/my-documents`);
  return unwrap(response);
};

export const getMyDocumentCategories = async () => {
  const response = await api.getData(`${BASE}/my-documents/categories`);
  return unwrap(response);
};

export const uploadMyDocument = async ({ categoryId, title, file }) => {
  const form = new FormData();
  form.append("categoryId", String(categoryId));
  form.append("title", title);
  form.append("file", file);
  const response = await api.postFormData(`${BASE}/my-documents`, form);
  return unwrap(response);
};

export const deleteMyDocument = async (id) => {
  const response = await api.deleteData(`${BASE}/my-documents/${id}`);
  return unwrap(response);
};

export const downloadMyDocument = async (id, fallbackName) => {
  const response = await api.getBlobData(`${BASE}/my-documents/${id}/download`);
  const blob = new Blob([response.data], {
    type: (response.headers && response.headers["content-type"]) || "application/octet-stream",
  });
  const name = filenameFromDisposition(
    response.headers && response.headers["content-disposition"],
    fallbackName || `document-${id}`
  );
  triggerDownload(blob, name);
};

export const getLockSettings = async () => {
  const response = await api.getData(`${BASE}/settings/lock`);
  return unwrap(response);
};

export const setLockSettings = async (enabled) => {
  const response = await api.putData(`${BASE}/settings/lock`, { enabled });
  return unwrap(response);
};

export const getComplianceMe = async () => {
  const response = await api.getData(`${BASE}/compliance/me`);
  return unwrap(response);
};

export const getNotifications = async (params) => {
  const response = await api.getData(
    `${BASE}/notifications${buildQuery(params)}`
  );
  return unwrap(response);
};

export const getUnreadNotificationCount = async () => {
  const response = await api.getData(`${BASE}/notifications/unread-count`);
  return unwrap(response);
};

export const markNotificationRead = async (id) => {
  const response = await api.postData(`${BASE}/notifications/${id}/read`, {});
  return unwrap(response);
};
